"use client";

import { motion } from "motion/react";
import type { Variants } from "motion/react";
import { ShieldCheck, Cpu, KeyRound, Clock, Crosshair } from "lucide-react";
import MaxWidthWrapper from "@/components/shared/max-width-wrapper";
import { HeaderSection } from "@/components/shared/header-section";
import { TiltCard } from "@/components/motion-ui/tilt-card";
import { useMotionUITheme, useMotionUITransition } from "@/components/motion-ui/ui-theme";

interface BentoGridProps {
  maxTilt?: number;
}

const SMALL_TILE_TILT = 1.15;

export default function BentoGrid({ maxTilt = 3.5 }: BentoGridProps) {
  const theme = useMotionUITheme();
  const still = theme.motionMode === "off";
  const calm = theme.motionMode === "calm";
  const uiTransition = useMotionUITransition("ui");

  const container: Variants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: calm ? 0 : theme.stagger.base,
      },
    },
  };

  const gridStagger: Variants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: calm ? 0 : theme.stagger.tight,
      },
    },
  };

  const item: Variants = {
    hidden: {
      opacity: 0,
      y: calm ? 0 : theme.travel.enter,
      scale: calm ? 1 : 0.98,
    },
    show: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { ...uiTransition },
    },
  };

  return (
    <section
      id="why-foulware"
      className="relative overflow-hidden pb-20"
      aria-labelledby="bentogrid-heading"
    >
      <MaxWidthWrapper>
        <motion.div
          className="flex flex-col gap-12"
          variants={still ? undefined : container}
          initial={still ? false : "hidden"}
          whileInView={still ? undefined : "show"}
          viewport={{ once: true, margin: "-40px" }}
        >
          {/* Section Header */}
          <motion.div variants={item} className="flex flex-col items-center text-center">
            <HeaderSection
              label="Why Foulware"
              title="Engineered for Undetected Domination"
              subtitle="Built with cutting-edge kernel bypasses and hardware-level emulation for total peace of mind."
            />
          </motion.div>

          {/* Bento Grid */}
          <motion.div
            className="grid grid-cols-1 gap-5 md:grid-cols-3 lg:gap-6"
            variants={gridStagger}
          >
            {/* Card 1 - Hero Feature (2 cols) */}
            <TiltCard
              item={item}
              interactive={!still}
              maxTilt={maxTilt}
              outerClassName="md:col-span-2"
              className="group relative flex h-full min-h-[320px] flex-col justify-between overflow-hidden rounded-[28px] border border-border/80 bg-gradient-to-b from-card via-card to-muted/20 p-8 shadow-sm transition-[border-color,box-shadow,background-color] duration-200 hover:border-indigo-500/40 hover:shadow-xl dark:border-border/60 dark:hover:border-indigo-500/50"
            >
              {/* Ambient Glow */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -right-12 -top-12 size-72 rounded-full bg-gradient-to-br from-indigo-500/15 via-purple-500/10 to-transparent blur-3xl transition-opacity duration-300 group-hover:opacity-100 dark:from-indigo-500/20"
              />

              <div className="relative z-10 flex flex-col items-start [transform:translateZ(12px)]">
                {/* Icon */}
                <div className="flex size-12 items-center justify-center rounded-2xl border border-indigo-500/25 bg-indigo-500/10 text-indigo-600 shadow-sm transition-transform duration-200 group-hover:scale-105 dark:border-indigo-400/25 dark:bg-indigo-950/50 dark:text-indigo-400">
                  <ShieldCheck className="size-6 stroke-[2]" />
                </div>

                {/* Badge & Headings */}
                <div className="mt-7">
                  <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-1 text-xs font-bold text-emerald-600 dark:text-emerald-400">
                    <span className="size-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.7)]" />
                    Zero Memory Signatures
                  </div>

                  <h3 className="mt-3.5 text-2xl font-extrabold tracking-tight text-foreground sm:text-3xl">
                    True Hardware & G-HUB Emulation
                  </h3>

                  <p className="mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base">
                    Unlike primitive software scripts that inject into game memory, our software simulates genuine physical mouse hardware through Logitech G-HUB and microcontroller interfaces, bypassing EAC, BattlEye, and heuristic anticheat scans.
                  </p>
                </div>
              </div>
            </TiltCard>

            {/* Card 2 - Instant Key Dispatch */}
            <TiltCard
              item={item}
              interactive={!still}
              maxTilt={maxTilt * SMALL_TILE_TILT}
              outerClassName="md:col-span-1"
              className="group relative flex h-full min-h-[280px] flex-col justify-between overflow-hidden rounded-[28px] border border-border/80 bg-gradient-to-b from-card via-card to-muted/20 p-8 shadow-sm transition-[border-color,box-shadow,background-color] duration-200 hover:border-purple-500/40 hover:shadow-xl dark:border-border/60 dark:hover:border-purple-500/50"
            >
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -right-10 -top-10 size-48 rounded-full bg-purple-500/10 blur-2xl transition-opacity duration-300 group-hover:opacity-100 dark:bg-purple-500/15"
              />

              <div className="relative z-10 flex flex-col items-start [transform:translateZ(12px)]">
                <div className="flex size-12 items-center justify-center rounded-2xl border border-purple-500/25 bg-purple-500/10 text-purple-600 shadow-sm transition-transform duration-200 group-hover:scale-105 dark:border-purple-400/25 dark:bg-purple-950/50 dark:text-purple-400">
                  <KeyRound className="size-6 stroke-[2]" />
                </div>

                <div className="mt-7">
                  <span className="text-xs font-bold uppercase tracking-wider text-purple-600 dark:text-purple-400">
                    Fulfillment
                  </span>
                  <h3 className="mt-2 text-xl font-bold tracking-tight text-foreground">
                    Instant Key Dispatch
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    Automated database key assignment delivers your license key within seconds. View, reveal, and copy keys straight from your user dashboard.
                  </p>
                </div>
              </div>
            </TiltCard>

            {/* Card 3 - Pixel-Perfect Recoil Curves */}
            <TiltCard
              item={item}
              interactive={!still}
              maxTilt={maxTilt * SMALL_TILE_TILT}
              outerClassName="md:col-span-1"
              className="group relative flex h-full min-h-[280px] flex-col justify-between overflow-hidden rounded-[28px] border border-border/80 bg-gradient-to-b from-card via-card to-muted/20 p-8 shadow-sm transition-[border-color,box-shadow,background-color] duration-200 hover:border-blue-500/40 hover:shadow-xl dark:border-border/60 dark:hover:border-blue-500/50"
            >
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -right-10 -top-10 size-48 rounded-full bg-blue-500/10 blur-2xl transition-opacity duration-300 group-hover:opacity-100 dark:bg-blue-500/15"
              />

              <div className="relative z-10 flex flex-col items-start [transform:translateZ(12px)]">
                <div className="flex size-12 items-center justify-center rounded-2xl border border-blue-500/25 bg-blue-500/10 text-blue-600 shadow-sm transition-transform duration-200 group-hover:scale-105 dark:border-blue-400/25 dark:bg-blue-950/50 dark:text-blue-400">
                  <Crosshair className="size-6 stroke-[2]" />
                </div>

                <div className="mt-7">
                  <span className="text-xs font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400">
                    Precision
                  </span>
                  <h3 className="mt-2 text-xl font-bold tracking-tight text-foreground">
                    Pixel-Perfect Recoil Curves
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    Individually calibrated spray tables for Beryl, M416, AK-47, AUG, and more. Humanized smoothing curves keep movement organic and spectator-safe.
                  </p>
                </div>
              </div>
            </TiltCard>

            {/* Card 4 - Rapid Patch Adapters */}
            <TiltCard
              item={item}
              interactive={!still}
              maxTilt={maxTilt * SMALL_TILE_TILT}
              outerClassName="md:col-span-1"
              className="group relative flex h-full min-h-[280px] flex-col justify-between overflow-hidden rounded-[28px] border border-border/80 bg-gradient-to-b from-card via-card to-muted/20 p-8 shadow-sm transition-[border-color,box-shadow,background-color] duration-200 hover:border-amber-500/40 hover:shadow-xl dark:border-border/60 dark:hover:border-amber-500/50"
            >
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -right-10 -top-10 size-48 rounded-full bg-amber-500/10 blur-2xl transition-opacity duration-300 group-hover:opacity-100 dark:bg-amber-500/15"
              />

              <div className="relative z-10 flex flex-col items-start [transform:translateZ(12px)]">
                <div className="flex size-12 items-center justify-center rounded-2xl border border-amber-500/25 bg-amber-500/10 text-amber-600 shadow-sm transition-transform duration-200 group-hover:scale-105 dark:border-amber-400/25 dark:bg-amber-950/50 dark:text-amber-400">
                  <Clock className="size-6 stroke-[2]" />
                </div>

                <div className="mt-7">
                  <span className="text-xs font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400">
                    Maintenance
                  </span>
                  <h3 className="mt-2 text-xl font-bold tracking-tight text-foreground">
                    Rapid Patch Adapters
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    Our 24/7 engineering team updates bypass methods within hours of any game patch, freezing active license durations during updates.
                  </p>
                </div>
              </div>
            </TiltCard>

            {/* Card 5 - Windows 10 & 11 Ready */}
            <TiltCard
              item={item}
              interactive={!still}
              maxTilt={maxTilt * SMALL_TILE_TILT}
              outerClassName="md:col-span-1"
              className="group relative flex h-full min-h-[280px] flex-col justify-between overflow-hidden rounded-[28px] border border-border/80 bg-gradient-to-b from-card via-card to-muted/20 p-8 shadow-sm transition-[border-color,box-shadow,background-color] duration-200 hover:border-emerald-500/40 hover:shadow-xl dark:border-border/60 dark:hover:border-emerald-500/50"
            >
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -right-10 -top-10 size-48 rounded-full bg-emerald-500/10 blur-2xl transition-opacity duration-300 group-hover:opacity-100 dark:bg-emerald-500/15"
              />

              <div className="relative z-10 flex flex-col items-start [transform:translateZ(12px)]">
                <div className="flex size-12 items-center justify-center rounded-2xl border border-emerald-500/25 bg-emerald-500/10 text-emerald-600 shadow-sm transition-transform duration-200 group-hover:scale-105 dark:border-emerald-400/25 dark:bg-emerald-950/50 dark:text-emerald-400">
                  <Cpu className="size-6 stroke-[2]" />
                </div>

                <div className="mt-7">
                  <span className="text-xs font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                    Compatibility
                  </span>
                  <h3 className="mt-2 text-xl font-bold tracking-tight text-foreground">
                    Windows 10 & 11 Ready
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    Seamless compatibility across all modern Windows versions (including 24H2) with Intel and AMD processors.
                  </p>
                </div>
              </div>
            </TiltCard>
          </motion.div>
        </motion.div>
      </MaxWidthWrapper>
    </section>
  );
}
